import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  lastSuspiciousTradeUpdated: null, // New state for last suspicious update
  btstAnalysis: {
    clients: [],
    summary: {
      totalBtstStbtTrades: 0,
      totalGrossPL: 0,
      totalClientsWithBtstStbt: 0,
    },
  },
  clientTradeDetails: {
    isLoading: false,
    data: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasNextPage: false,
    },
    clientInfo: null,
  },
  clientIntradayDetails: {
    isLoading: false,
    data: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasNextPage: false,
    },
    clientInfo: null,
  },
  clientOrderDetails: {
    isLoading: false,
    data: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasNextPage: false,
    },
    clientInfo: null,
  },
  intradayAnalysis: {
    clients: [],
    summary: {},
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalClients: 0,
      limit: 50,
      hasNext: false,
      hasPrev: false,
    },
  },
  orderTypesAnalysis: {
    clients: [],
    weeklyData: {},
    dailyData: [],
    summary: {},
  },
  ipTrackingAnalysis: {
    ipAnalysis: [],
    sharedIPs: [],
    summary: {
      totalSharedIpAddress: 0,
      sharedIpAddress: 0,
      totalTradesAnalyzed: 0,
      totalTradersWithSharedIPs: 0,
    },
  },
  openTradeAnalysis: {
    trades: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalTrades: 0,
      limit: 50,
      hasNext: false,
      hasPrev: false,
    },
    filters: {
      nsePercentage: null,
      mcxPointValue: 2000,
      includeLossTrade: false,
    },
  },
  profitableClientsAnalysis: {
    creators: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      limit: 50,
      hasNext: false,
      hasPrev: false,
    },
    filters: {
      lastWeeks: 5,
      currentYear: 2025,
      currentWeek: 40,
      currentDay: 5,
      startingWeek: 39,
      weeksAnalyzed: [],
    },
    summary: {
      totalUsersWithPositivePL: 0,
      weeksAnalyzed: 0,
      averageGrossPL: 0,
    },
  },
  highBrokerageClientsAnalysis: {
    users: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      limit: 50,
      hasNext: false,
      hasPrev: false,
    },
    filters: {
      nseBrokerageThreshold: 0,
      mcxBrokerageThreshold: 300,
    },
    summary: {
      totalUsers: 0,
      totalGrossPL: 0,
      totalBrokerage: 0,
      totalNSEBrokerage: 0,
      totalMCXBrokerage: 0,
      averageGrossPL: 0,
      averageBrokerage: 0,
    },
  },
  timeRangeTradesAnalysis: {
    trades: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalTrades: 0,
      limit: 50,
      hasNext: false,
      hasPrev: false,
    },
    filters: {
      startTime: '09:00',
      endTime: '09:45',
    },
    summary: {
      totalTrades: 0,
      tradeTypes: {
        OPEN: 0,
        ORDER: 0,
      },
    },
  },
  ipTradeDetails: {
    isLoading: false,
    data: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasNextPage: false,
    },
    ipAddress: null,
  },
  tradeAlerts: {
    trades: [],
    pagination: {
      hasNextPage: false,
      nextCursor: null,
      limit: 20,
      count: 0,
    },
    selectedAlert: null,
  },
  alertTypes: {
    data: [],
    isLoading: false,
    isLoaded: false,
  },
  users: {
    admin: [],
    broker: [],
    client: [],
  },
  usersLoaded: {
    admin: false,
    broker: false,
    client: false,
  },
};

const slice = createSlice({
  name: 'suspiciousActivity',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    }, // GET BTST/STBT ANALYSIS SUCCESS
    getBtstAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.btstAnalysis = action.payload;
    },

    // CLIENT TRADE DETAILS ACTIONS
    startClientDetailsLoading(state) {
      state.clientTradeDetails.isLoading = true;
    },

    stopClientDetailsLoading(state) {
      state.clientTradeDetails.isLoading = false;
    },

    getClientTradeDetailsSuccess(state, action) {
      state.clientTradeDetails.isLoading = false;
      state.clientTradeDetails.data = action.payload.trades;
      state.clientTradeDetails.pagination = action.payload.pagination;
      state.clientTradeDetails.clientInfo = action.payload.user;
    },
    clearClientTradeDetails(state) {
      state.clientTradeDetails.data = null;
      state.clientTradeDetails.clientInfo = null;
      state.clientTradeDetails.pagination = {
        page: 1,
        limit: 20,
        total: 0,
        hasNextPage: false,
      };
    },

    // CLIENT INTRADAY DETAILS ACTIONS
    startClientIntradayDetailsLoading(state) {
      state.clientIntradayDetails.isLoading = true;
    },

    stopClientIntradayDetailsLoading(state) {
      state.clientIntradayDetails.isLoading = false;
    },

    getClientIntradayDetailsSuccess(state, action) {
      state.clientIntradayDetails.isLoading = false;
      state.clientIntradayDetails.data = action.payload.trades;
      state.clientIntradayDetails.pagination = action.payload.pagination;
      state.clientIntradayDetails.clientInfo = action.payload.user;
    },

    clearClientIntradayDetails(state) {
      state.clientIntradayDetails.data = null;
      state.clientIntradayDetails.clientInfo = null;
      state.clientIntradayDetails.pagination = {
        page: 1,
        limit: 20,
        total: 0,
        hasNextPage: false,
      };
    },

    // CLIENT ORDER DETAILS ACTIONS
    startClientOrderDetailsLoading(state) {
      state.clientOrderDetails.isLoading = true;
    },

    stopClientOrderDetailsLoading(state) {
      state.clientOrderDetails.isLoading = false;
    },

    getClientOrderDetailsSuccess(state, action) {
      state.clientOrderDetails.isLoading = false;
      state.clientOrderDetails.data = action.payload.trades;
      state.clientOrderDetails.pagination = action.payload.pagination;
      state.clientOrderDetails.clientInfo = action.payload.user;
    },

    clearClientOrderDetails(state) {
      state.clientOrderDetails.data = null;
      state.clientOrderDetails.clientInfo = null;
      state.clientOrderDetails.pagination = {
        page: 1,
        limit: 20,
        total: 0,
        hasNextPage: false,
      };
    },

    // GET INTRADAY ANALYSIS SUCCESS
    getIntradayAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.intradayAnalysis = action.payload;
    },

    // GET ORDER TYPES ANALYSIS SUCCESS
    getOrderTypesAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.orderTypesAnalysis = action.payload;
    },

    // GET IP TRACKING ANALYSIS SUCCESS
    getIpTrackingAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.ipTrackingAnalysis = action.payload;
    },

    // GET OPEN TRADE ANALYSIS SUCCESS
    getOpenTradeAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.openTradeAnalysis = action.payload;
    },

    // GET PROFITABLE CLIENTS ANALYSIS SUCCESS
    getProfitableClientsAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.profitableClientsAnalysis = action.payload;
    },

    // GET HIGH BROKERAGE CLIENTS ANALYSIS SUCCESS
    getHighBrokerageClientsAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.highBrokerageClientsAnalysis = action.payload;
    },

    // GET TIME RANGE TRADES ANALYSIS SUCCESS
    getTimeRangeTradesAnalysisSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.timeRangeTradesAnalysis = action.payload;
    },

    // GET IP TRADE DETAILS SUCCESS
    getIpTradeDetailsSuccess(state, action) {
      state.ipTradeDetails.isLoading = false;
      state.ipTradeDetails.data = action.payload.trades;
      state.ipTradeDetails.pagination = action.payload.pagination;
      state.ipTradeDetails.ipAddress = action.payload.ipAddress;
    },

    // START IP TRADE DETAILS LOADING
    startIpTradeDetailsLoading(state) {
      state.ipTradeDetails.isLoading = true;
    },

    // CLEAR IP TRADE DETAILS
    clearIpTradeDetails(state) {
      state.ipTradeDetails.data = null;
      state.ipTradeDetails.ipAddress = null;
      state.ipTradeDetails.pagination = {
        page: 1,
        limit: 20,
        total: 0,
        hasNextPage: false,
      };
    },

    getUsersByRoleSuccess(state, action) {
      const { role, users } = action.payload;
      state.users[role] = users;
      state.usersLoaded[role] = true;
    },

    // GET TRADE ALERTS SUCCESS
    getTradeAlertsSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      const { isFirstPage, data } = action.payload;

      if (isFirstPage) {
        state.tradeAlerts.trades = data.trades;
      } else {
        state.tradeAlerts.trades = [...state.tradeAlerts.trades, ...data.trades];
      }

      state.tradeAlerts.pagination = data.pagination;
      state.tradeAlerts.totalProfit = data.totalProfit || 0;
    },

    // SET SELECTED ALERT
    setSelectedAlert(state, action) {
      state.tradeAlerts.selectedAlert = action.payload;
    }, // CLEAR TRADE ALERTS
    clearTradeAlerts(state) {
      state.tradeAlerts.trades = [];
      state.tradeAlerts.pagination = {
        hasNextPage: false,
        nextCursor: null,
        limit: 20,
        count: 0,
      };
      state.tradeAlerts.selectedAlert = null;
    },

    // ALERT TYPES REDUCERS
    startLoadingAlertTypes(state) {
      state.alertTypes.isLoading = true;
    },

    getAlertTypesSuccess(state, action) {
      state.alertTypes.isLoading = false;
      state.alertTypes.data = action.payload;
      state.alertTypes.isLoaded = true;
    },

    getAlertTypesError(state) {
      state.alertTypes.isLoading = false;
    },

    // RESET USERS FOR ROLE
    resetUsersForRole(state, action) {
      const { role } = action.payload;
      state.users[role] = [];
      state.usersLoaded[role] = false;
    },

    // RESET DEPENDENT USERS (used for hierarchical clearing)
    resetDependentUsers(state, action) {
      const { roles } = action.payload;
      roles.forEach((role) => {
        state.users[role] = [];
        state.usersLoaded[role] = false;
      });
    },

    // CLEAR ERROR
    clearError(state) {
      state.error = null;
    },
    setLastSuspiciousTradeUpdated(state, action) {
      state.lastSuspiciousTradeUpdated = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  clearError,
  resetUsersForRole,
  resetDependentUsers,
  setSelectedAlert,
  clearTradeAlerts,
  startLoadingAlertTypes,
  getAlertTypesSuccess,
  getAlertTypesError,
  clearClientTradeDetails,
  clearClientIntradayDetails,
  clearClientOrderDetails,
  setLastSuspiciousTradeUpdated,
  clearIpTradeDetails,
} = slice.actions;

// ----------------------------------------------------------------------

export function getBtstStbtAnalysis(filters = {}) {
  return async () => {
    const hasUserId = filters.userId;

    if (hasUserId) {
      dispatch(slice.actions.startClientDetailsLoading());
    } else {
      dispatch(slice.actions.startLoading());
    }

    try {
      const response = await axios.get('/user/btst-stbt-analysis', {
        params: filters,
      });

      if (response.data && response.data.data) {
        if (hasUserId) {
          console.log('Client trade details response:', response.data.data);
          // Store in client trade details for modal
          dispatch(slice.actions.getClientTradeDetailsSuccess(response.data.data));
        } else {
          // Store in main analysis data
          dispatch(slice.actions.getBtstAnalysisSuccess(response.data.data));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching BTST/STBT analysis:', error);
      if (!hasUserId) {
        // Only show error in main tab, not for modal
        dispatch(slice.actions.hasError(error.message || 'Failed to fetch BTST/STBT analysis'));
      }
    }
  };
}

export function getIntradayAnalysis(filters = {}) {
  return async () => {
    const hasUserId = filters.userId;

    if (hasUserId) {
      dispatch(slice.actions.startClientIntradayDetailsLoading());
    } else {
      dispatch(slice.actions.startLoading());
    }

    try {
      const response = await axios.get('/user/intraday-analysis', {
        params: filters,
      });

      if (response.data && response.data.data) {
        if (hasUserId) {
          // Store in client intraday details for modal
          dispatch(slice.actions.getClientIntradayDetailsSuccess(response.data.data));
        } else {
          // Store in main analysis data
          dispatch(slice.actions.getIntradayAnalysisSuccess(response.data.data));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching intraday analysis:', error);
      if (!hasUserId) {
        // Only show error in main tab, not for modal
        dispatch(slice.actions.hasError(error.message || 'Failed to fetch intraday analysis'));
      }
    }
  };
}

export function getOrderTypesAnalysis(filters = {}) {
  return async () => {
    const hasUserId = filters.userId;

    if (hasUserId) {
      dispatch(slice.actions.startClientOrderDetailsLoading());
    } else {
      dispatch(slice.actions.startLoading());
    }

    try {
      const response = await axios.get('/user/limit-analysis', {
        params: filters,
      });

      if (response.data && response.data.data) {
        if (hasUserId) {
          console.log('Client order details response:', response.data.data);
          // Store in client order details for modal
          dispatch(slice.actions.getClientOrderDetailsSuccess(response.data.data));
        } else {
          // Store in main analysis data
          dispatch(slice.actions.getOrderTypesAnalysisSuccess(response.data.data));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching limit analysis:', error);
      if (!hasUserId) {
        // Only show error in main tab, not for modal
        dispatch(slice.actions.hasError(error.message || 'Failed to fetch limit analysis'));
      }
    }
  };
}

export function getIpTrackingAnalysis(filters = {}) {
  return async () => {
    const hasIpAddress = filters.ipAddress;

    if (hasIpAddress) {
      dispatch(slice.actions.startIpTradeDetailsLoading());
    } else {
      dispatch(slice.actions.startLoading());
    }

    try {
      const response = await axios.get('/user/ip-analysis', {
        params: filters,
      });

      if (response.data && response.data.data) {
        if (hasIpAddress) {
          // Store in IP trade details for modal
          const responseData = response.data.data;
          dispatch(
            slice.actions.getIpTradeDetailsSuccess({
              trades: responseData.trades || [],
              pagination: responseData.pagination || {
                page: 1,
                limit: 50,
                total: (responseData.trades || []).length,
                hasNextPage: false,
              },
              ipAddress: filters.ipAddress,
            })
          );
        } else {
          // Store in main analysis data
          dispatch(slice.actions.getIpTrackingAnalysisSuccess(response.data.data));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching IP tracking analysis:', error);
      if (!hasIpAddress) {
        // Only show error in main tab, not for modal
        dispatch(slice.actions.hasError(error.message || 'Failed to fetch IP tracking analysis'));
      }
    }
  };
}

// Alias for backward compatibility
export const getIpTradeDetails = getIpTrackingAnalysis;

export function getOpenTradeAnalysis(filters = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get('/user/open-trade-analysis', {
        params: filters,
      });

      if (response.data && response.data.data) {
        dispatch(slice.actions.getOpenTradeAnalysisSuccess(response.data.data));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching open trade analysis:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch open trade analysis'));
    }
  };
}

export function getProfitableClientsAnalysis(filters = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get('/user/users-positive-pl-weeks', {
        params: filters,
      });

      if (response.data && response.data.data) {
        dispatch(slice.actions.getProfitableClientsAnalysisSuccess(response.data.data));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching profitable clients analysis:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch profitable clients analysis'));
    }
  };
}

export function getHighBrokerageClientsAnalysis(filters = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get('/user/users-brokerage-threshold', {
        params: filters,
      });

      if (response.data && response.data.data) {
        dispatch(slice.actions.getHighBrokerageClientsAnalysisSuccess(response.data.data));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching high brokerage clients analysis:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch high brokerage clients analysis'));
    }
  };
}

export function getTimeRangeTradesAnalysis(filters = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get('/user/timing-trades', {
        params: filters,
      });

      if (response.data && response.data.data) {
        dispatch(slice.actions.getTimeRangeTradesAnalysisSuccess(response.data.data));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching time range trades analysis:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch time range trades analysis'));
    }
  };
}

export function getTradeAlerts(filters = {}, cursor = null, isFirstPage = true) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const params = {
        ...filters,
        ...(cursor && { cursor }),
      };

      const response = await axios.get('/trade-alerts/trades-with-alerts', {
        params,
      });

      if (response.data && response.data.data) {
        dispatch(
          slice.actions.getTradeAlertsSuccess({
            data: response.data.data,
            isFirstPage,
          })
        );
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching trade alerts:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch trade alerts'));
    }
  };
}

export function getUsersByRole(role, params = {}, forceRefetch = false) {
  return async (dispatch, getState) => {
    try {
      const state = getState().suspiciousActivity;

      // If we're not forcing a refetch and data is already loaded with same params, skip
      if (!forceRefetch && state.usersLoaded[role] && Object.keys(params).length === 0) {
        return;
      }

      const response = await axios.get(`/user/all/${role}`, {
        params,
      });

      if (response.data && response.data.users) {
        dispatch(
          slice.actions.getUsersByRoleSuccess({
            role,
            users: response.data.users,
          })
        );
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error(`Error fetching users for role ${role}:`, error);
      dispatch(slice.actions.hasError(error.message || `Failed to fetch users for role ${role}`));
    }
  };
}

// ----------------------------------------------------------------------

export function getAlertTypes() {
  return async () => {
    dispatch(slice.actions.startLoadingAlertTypes());
    try {
      const response = await axios.get('/trade-alerts/types');

      if (response.data && response.data.data && response.data.data.alertTypes) {
        dispatch(slice.actions.getAlertTypesSuccess(response.data.data.alertTypes));
      } else {
        throw new Error('Invalid alert types response format');
      }
    } catch (error) {
      console.error('Error fetching alert types:', error);
      dispatch(slice.actions.getAlertTypesError());
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch alert types'));
    }
  };
}

// Thunk to fetch last suspicious trade updated time
export function getLastSuspiciousTradeUpdated() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/trade-alerts/last-updated');
      if (response.data && response.data.data && response.data.data.lastSuspiciousActivityUpdated) {
        dispatch(slice.actions.setLastSuspiciousTradeUpdated(response.data.data.lastSuspiciousActivityUpdated));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching last suspicious trade updated:', error);
      // Optionally, you can dispatch an error action here
    }
  };
}
